import { NgClass, NgStyle, NgTemplateOutlet } from '@angular/common';
import {
  AfterViewInit,
  Component,
  HostListener,
  OnChanges,
  OnInit,
  SimpleChanges,
  viewChild,
  input,
  output,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatSortModule, Sort } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FlexLayoutModule } from '@ngbracket/ngx-layout';
import { TranslatePipe } from '@ngx-translate/core';
import { ServiceTranslated } from 'src/app/models/serviceTranslated';

@Component({
  selector: 'app-services-list',
  imports: [
    MatIconModule,
    NgClass,
    NgStyle,
    MatTooltipModule,
    MatButtonModule,
    FlexLayoutModule,
    TranslatePipe,
    MatTableModule,
    MatSortModule,
    MatPaginatorModule,
    NgTemplateOutlet,
  ],
  templateUrl: './services-list.component.html',
  styleUrl: './services-list.component.scss',
})
export class ServicesListComponent implements OnChanges, OnInit, AfterViewInit {
  readonly services = input.required<ServiceTranslated[]>();
  readonly filter = input('');
  readonly accessButtonClick = output<{
    url: string;
    title: string;
  }>();
  readonly helpButtonClick = output<string>();

  readonly paginator = viewChild(MatPaginator);

  dataSource: MatTableDataSource<ServiceTranslated>;
  displayedColumns: string[] = [];
  expandedElement: ServiceTranslated | null;
  private interalSort: Sort | null;
  sortedValue: string;
  windowWidth: number = window.innerWidth;

  ngOnInit(): void {
    this.updateDisplayedColumns();
    this.dataSource = new MatTableDataSource(this.services());
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['services']) {
      if (this.interalSort) {
        this.sortData(this.interalSort);
      }
    }
    if (changes['filter']) {
      this.applyFilter(changes['filter'].currentValue);
      if (this.interalSort) {
        this.sortData(this.interalSort);
      }
    }
  }

  ngAfterViewInit(): void {
    if (this.dataSource) this.dataSource.paginator = this.paginator();
  }

  @HostListener('window:resize')
  onResize(): void {
    this.updateDisplayedColumns();
    this.windowWidth = window.innerWidth;
  }

  private applyFilter(filter: string) {
    if (this.dataSource) {
      if (!filter) {
        filter = '';
      }
      this.dataSource.filter = filter.trim().toLocaleLowerCase();
      if (this.dataSource.paginator) {
        this.dataSource.paginator.firstPage();
      }
    }
  }

  private updateDisplayedColumns(): void {
    if (window.innerWidth <= 400) {
      this.displayedColumns = ['title', 'accessurl', 'expand'];
    } else if (window.innerWidth > 400 && window.innerWidth <= 550) {
      this.displayedColumns = ['jpegPhoto', 'title', 'accessurl', 'expand'];
    } else if (window.innerWidth > 550 && window.innerWidth <= 800) {
      this.displayedColumns = [
        'jpegPhoto',
        'title',
        'help',
        'accessurl',
        'expand',
      ];
    } else {
      this.displayedColumns = [
        'jpegPhoto',
        'title',
        'description',
        'help',
        'accessurl',
      ];
    }
  }

  openService(service: ServiceTranslated): void {
    this.accessButtonClick.emit({
      url: service.accessurl,
      title: service.title,
    });
  }

  openDocumentation(helpUrl: string): void {
    this.helpButtonClick.emit(helpUrl);
  }

  sortData(sort: Sort) {
    this.sortedValue = sort.active;
    this.interalSort = sort;
    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }

    if (!sort.active || sort.direction === '') {
      this.sortedValue = null;
      const sortedServices = this.services().sort(
        (a: ServiceTranslated, b: ServiceTranslated) => {
          return a.priority - b.priority;
        }
      );
      this.dataSource.data = sortedServices;
      return;
    }

    this.dataSource.data = this.services().sort((a, b) => {
      const isAsc = sort.direction === 'asc';
      switch (sort.active) {
        case 'title':
          return this.compare(
            a.title.toLocaleLowerCase(),
            b.title.toLocaleLowerCase(),
            isAsc
          );
      }
    });
  }

  private compare(a: number | string, b: number | string, isAsc: boolean) {
    return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
  }

  toggleRow(element: ServiceTranslated): void {
    this.expandedElement = this.expandedElement === element ? null : element;
  }
}
